import { flexRender, Table as TanstackTable } from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../../../../design-system/button/button';
import { Inline } from '../../../../../design-system/inline/inline';
import { Stack } from '../../../../../design-system/stack/stack';
import { Table, TableBody, TableCell, TableRow } from '../../../../../design-system/table/table';
import { InvoiceEvidenceView } from '../../../../../types/entity-types';
import {
  APPROVE_COLUMN_ID,
  IS_PAID_CONFIDENCE_SCORE_COLUMN_ID,
  REJECT_COLUMN_ID,
  SELECT_COLUMN_ID,
  VIEW_INVOICE_COLUMN_ID,
} from './review-matches-table-columns-definition';

type ReviewMatchesTableProps = {
  table: TanstackTable<InvoiceEvidenceView>;
  onSelectMenuItem: <MenuItemVariant>(payload: { entityId: string; menuItemVariant: MenuItemVariant }) => void;
};

export const ReviewMatchesTable = ({ table, onSelectMenuItem }: ReviewMatchesTableProps) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const [tableWidth, setTableWidth] = useState(0);

  useEffect(() => {
    const div = tableRef.current;

    if (!div) {
      return;
    }

    const { width } = div.getBoundingClientRect();

    setTableWidth(width);
  }, []);

  return (
    <Table ref={tableRef} className="-mt-3 border-separate border-spacing-y-3">
      <TableBody>
        {table.getRowModel().rows.map((row) => {
          const selectCell = row.getVisibleCells().find((cell) => cell.column.id === SELECT_COLUMN_ID);
          const confidenceCell = row
            .getVisibleCells()
            .find((cell) => cell.column.id === IS_PAID_CONFIDENCE_SCORE_COLUMN_ID);
          const viewInvoiceCell = row.getVisibleCells().find((cell) => cell.column.id === VIEW_INVOICE_COLUMN_ID);
          const approveCell = row.getVisibleCells().find((cell) => cell.column.id === APPROVE_COLUMN_ID);
          const rejectCell = row.getVisibleCells().find((cell) => cell.column.id === REJECT_COLUMN_ID);
          const secondRowCells = row.getVisibleCells().filter((c) => c.column.columnDef.meta?.inSecondRow);

          return (
            <TableRow key={row.id} className="w-full hover:bg-transparent">
              <TableCell colSpan={row.getVisibleCells().length} className="p-0">
                <Stack className="overflow-clip rounded-lg border-[0.5px] border-border">
                  <Inline justify="between" className="w-full bg-muted px-4 py-3">
                    <Inline space={2}>
                      {selectCell ? flexRender(selectCell.column.columnDef.cell, selectCell.getContext()) : null}

                      <div className="p-0">
                        {confidenceCell
                          ? flexRender(confidenceCell.column.columnDef.cell, confidenceCell.getContext())
                          : null}
                      </div>

                      <div className="p-0">
                        {viewInvoiceCell
                          ? flexRender(viewInvoiceCell.column.columnDef.cell, viewInvoiceCell.getContext())
                          : null}
                      </div>
                    </Inline>

                    <Inline space={2.5}>
                      {approveCell && row.original.transactionId
                        ? flexRender(approveCell.column.columnDef.cell, approveCell.getContext())
                        : null}

                      {rejectCell ? flexRender(rejectCell.column.columnDef.cell, rejectCell.getContext()) : null}
                    </Inline>
                  </Inline>

                  <Stack className="w-full bg-white">
                    <Inline justify="between" align="stretch" space={0} wrap="nowrap" className="w-full">
                      {secondRowCells.map((cell) => {
                        const sumSize = table.getTotalSize();
                        const cellSize = cell.column.getSize();
                        const cellWidthPercentage = cellSize / sumSize;
                        const finalWidth = tableWidth > 0 ? cellWidthPercentage * tableWidth : undefined;

                        return (
                          <div
                            key={cell.column.id}
                            className="p-4"
                            style={{
                              width: finalWidth ? `${finalWidth}px` : undefined,
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        );
                      })}
                    </Inline>

                    {!row.original.transactionId && (
                      <div className="pb-5 pl-4">
                        <Button
                          variant="link"
                          className="p-0 text-link"
                          onClick={() =>
                            onSelectMenuItem({ entityId: row.original.id, menuItemVariant: 'CHANGE_TRANSACTION_MATCH' })
                          }
                        >
                          Set matched transactions
                        </Button>
                      </div>
                    )}
                  </Stack>
                </Stack>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
